import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Upload, Download, Trash2, File, Loader2, Link, Pencil, Check, X } from 'lucide-react';
import { api, Upload as UploadType } from '../utils/api';
import { toast } from '../hooks/use-toast';

export interface ActiveFetch {
  fetchId: string;
  filename: string;
  bytesReceived: number;
  totalBytes: number;
  percent: number | null;
  status?: 'connecting' | 'downloading' | 'storing' | 'error';
  error?: string;
}

interface Props {
  pageId: string;
  uploads: UploadType[];
  activeFetches: ActiveFetch[];
  onUploadsChange: (uploads: UploadType[]) => void;
  onFetchStart: (fetchId: string) => void;
}

const MAX_BYTES = 50 * 1024 * 1024;

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function ProgressBar({ percent, status }: { percent: number | null; status?: string }) {
  if (status === 'error') {
    return (
      <div className="h-1.5 w-full rounded-full bg-destructive/20 overflow-hidden">
        <div className="h-full w-full bg-destructive" />
      </div>
    );
  }
  if (percent === null) {
    return (
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full w-full bg-primary animate-pulse" />
      </div>
    );
  }
  return (
    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
      <div
        className="h-full bg-primary transition-all duration-300 ease-out"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

function FileRow({
  upload,
  pageId,
  onUpdate,
  onDelete,
}: {
  upload: UploadType;
  pageId: string;
  onUpdate: (u: UploadType) => void;
  onDelete: (u: UploadType) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(upload.filename);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const startEdit = () => {
    setDraft(upload.filename);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(upload.filename);
  };

  const saveEdit = async () => {
    const clean = draft.trim();
    if (!clean || clean === upload.filename) { cancelEdit(); return; }
    setSaving(true);
    try {
      const updated = await api.renameUpload(pageId, upload.id, clean);
      onUpdate(updated);
      setEditing(false);
    } catch (err: any) {
      toast({ title: 'Rename failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteUpload(pageId, upload.id);
      onDelete(upload);
      toast({ title: 'File deleted', description: upload.filename });
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
      setDeleting(false);
    }
  };

  return (
    <li className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm min-w-0">
      <File className="h-4 w-4 shrink-0 text-muted-foreground" />

      {editing ? (
        <>
          <Input
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-6 flex-1 min-w-0 text-sm py-0 px-1"
            disabled={saving}
          />
          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" title="Save" onClick={saveEdit} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 text-green-600" />}
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" title="Cancel" onClick={cancelEdit} disabled={saving}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </>
      ) : (
        <>
          <span
            className="flex-1 truncate font-medium cursor-pointer hover:text-primary min-w-0"
            title="Click to rename"
            onClick={startEdit}
          >
            {upload.filename}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(upload.file_size)}</span>
          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" title="Rename" onClick={startEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <a href={upload.downloadUrl} target="_blank" rel="noopener noreferrer" download={upload.filename}>
            <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" title="Download">
              <Download className="h-3.5 w-3.5" />
            </Button>
          </a>
          <Button
            size="icon" variant="ghost"
            className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
            title="Delete"
            disabled={deleting}
            onClick={handleDelete}
          >
            {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        </>
      )}
    </li>
  );
}

export function UploadSection({ pageId, uploads, activeFetches, onUploadsChange, onFetchStart }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showFetchInput, setShowFetchInput] = useState(false);
  const [fetchUrl, setFetchUrl] = useState('');
  const [fetching, setFetching] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_BYTES) {
      toast({ title: 'File too large', description: 'Maximum upload size is 50 MB.', variant: 'destructive' });
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const upload = await api.uploadFile(pageId, file);
      onUploadsChange([upload, ...uploads]);
      toast({ title: 'File uploaded', description: file.name });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message || 'Could not upload file.', variant: 'destructive' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleFetchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = fetchUrl.trim();
    if (!url) return;

    setFetching(true);
    try {
      const { fetchId } = await api.fetchUrl(pageId, url);
      onFetchStart(fetchId);
      setFetchUrl('');
      setShowFetchInput(false);
    } catch (err: any) {
      toast({ title: 'Fetch failed', description: err.message || 'Could not start fetch.', variant: 'destructive' });
    } finally {
      setFetching(false);
    }
  };

  const statusLabel = (f: ActiveFetch) => {
    if (f.status === 'error') return f.error || 'Error';
    if (f.status === 'storing') return 'Saving to storage…';
    if (f.status === 'connecting' || (!f.bytesReceived && f.percent === null)) return 'Connecting…';
    if (f.percent !== null) return `${f.percent}%`;
    return formatBytes(f.bytesReceived);
  };

  const hasContent = uploads.length > 0 || activeFetches.length > 0;

  return (
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">
            Files {hasContent ? `(${uploads.length})` : ''}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowFetchInput(v => !v); setFetchUrl(''); }}
            >
              <Link className="h-4 w-4 mr-2" />
              Fetch URL
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? 'Uploading…' : 'Upload File'}
            </Button>
          </div>
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />
        </div>

        {showFetchInput && (
          <form onSubmit={handleFetchSubmit} className="flex gap-2 mt-2">
            <Input
              type="url"
              placeholder="https://example.gov.uk/document.pdf"
              value={fetchUrl}
              onChange={e => setFetchUrl(e.target.value)}
              className="flex-1 text-sm"
              autoFocus
            />
            <Button type="submit" size="sm" disabled={fetching || !fetchUrl.trim()}>
              {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fetch'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowFetchInput(false)}>
              Cancel
            </Button>
          </form>
        )}
      </CardHeader>

      {hasContent && (
        <CardContent className="pt-0">
          <ul className="space-y-2">
            {activeFetches.map(f => (
              <li key={f.fetchId} className="rounded-md border px-3 py-2 space-y-1.5">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className={`h-4 w-4 shrink-0 text-muted-foreground ${f.status !== 'error' ? 'animate-spin' : ''}`} />
                  <span className="flex-1 truncate font-medium text-muted-foreground">
                    {f.filename || 'Connecting…'}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {f.totalBytes > 0 ? formatBytes(f.totalBytes) : ''}
                  </span>
                </div>
                <ProgressBar percent={f.percent} status={f.status === 'error' ? 'error' : undefined} />
                <p className="text-xs text-muted-foreground">{statusLabel(f)}</p>
              </li>
            ))}

            {uploads.map(u => (
              <FileRow
                key={u.id}
                upload={u}
                pageId={pageId}
                onUpdate={updated => onUploadsChange(uploads.map(x => x.id === updated.id ? updated : x))}
                onDelete={deleted => onUploadsChange(uploads.filter(x => x.id !== deleted.id))}
              />
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  );
}
